"use strict";

// Copyright 2011-2019 The MathWorks, Inc.
define(['dojo/_base/connect', 'dojo/_base/declare', 'dojo/_base/Deferred', 'dojo/_base/lang', 'dojo/aspect', 'dojo/dom-construct', 'mw-log/Log', './CheckUrlService'], function (connect, declare, Deferred, lang, aspect, domConstruct, Log, CheckUrlService) {
  var USE_LATEST_VERSION = 'use_latest';
  var IframeProxyClient = declare([], {
    /*
        If you are editing the REMOTE Proxy Version there are a number of thing you must do
        1) Create Gecks for Teams using the existing Remote Proxy to update:
        * UDC - Websites - External Web Services :: Usage Data Collector
        ** E.g. http://komodo.mathworks.com/main/gecko/view?Record=2058826
        * Doc Center Search - Website - Product Help :: Search
        ** E.g. http://komodo.mathworks.com/main/gecko/view?Record=1636156
         You also need to update REMOTE Proxy in the MATLAB Sandbox.
    */
    IFRAME_TIMEOUT: 20000,
    LATEST_VERSION: '1.6',
    REMOTE_PROXY: '/remote/proxy/',
    constructor: function constructor(iframeId) {
      this.iframeId = iframeId || 'remoteProducer';
      this._version = null;
      this._messageId = 0;
      this._loaded = false;
      this._pendingQueue = [];
      this._transactions = {};
      this._uploadTargetCallbacks = {};
      this._checkUrl = null;
      this._loadTimeoutHandle = null;
      this._checkUrlTimeout = null;
      this._errorRate = 0;
    },

    /**
    * Creates an new iframe for remote communication and sets the src URL
    * the iframe.  If this function was already called, it only creates a new
    * iframe if the existing iframe's src would have to change.
    *
    * @param {string} host              e.g. https://something.com/ -
    *                                       undefined and "" are normalized to "/"
    *                                       if host does not end in a trailing slash it is
    *                                       normalized to have a trailing slash
    * @param {string} key               Sets "routingkey" on url.  Used by the workerrouter
    *                                       to route to a worker.  Treats undefined and ""
    *                                       the same.
    * @param {string} computerToken     no longer used. Pass ""
    *                                       if refactoring - remove computeToken
    * @param {string} proxyUrl          a path that overrides the standard "remote/proxy" path
    *                                       if proxyUrl is set and not "", the iframe src will
    *                                       point to:  host+proxyUrl
    *
    */
    setRemotePageHost: function setRemotePageHost(host, key, computeToken, proxyUrl) {
      host = this._combineUriPaths(host || '', '/');
      key = key || '';
      proxyUrl = proxyUrl || '';
      this.computeToken = computeToken;

      if (host === this._host && key === this._key && proxyUrl === this._proxyUrl) {
        return;
      }

      this._loaded = false;
      this._errorLoading = false;
      this._version = null;
      this._host = host;
      this._key = key;
      this._proxyUrl = proxyUrl;

      this._setVersion(USE_LATEST_VERSION);
    },
    _setVersion: function _setVersion(version) {
      if (!version || version === USE_LATEST_VERSION) {
        version = this.LATEST_VERSION;
      } // Only try to reload if version changed and we are not loaded.
      // This occurs when we fail to load a particular version OR
      // setRemotePageHost is called with a different "host", "key", or "proxyUrl"
      // setRemotePageHost sets this._version to null so version !== this._version


      if (this._loaded || version === this._version) {
        return;
      }

      this._version = version;

      var calculatedUrls = this._calculateUrls(this._host, this._key, version, this._proxyUrl);

      this._createIframe(calculatedUrls);
    },
    _calculateUrls: function _calculateUrls(host, key, version, proxyUrl) {
      var iFrameUrl = this.REMOTE_PROXY + version + '/iframeProxyRelease.html';
      var fastFailTestUrl = this.REMOTE_PROXY + version + '/CheckJsFile.js';

      if (proxyUrl) {
        iFrameUrl = proxyUrl;
      }

      iFrameUrl = this._combineUriPaths(host, iFrameUrl).split('?')[0];
      iFrameUrl += key ? '?routingkey=' + key : ''; // Uncomment this code when g1032962   is fixed.
      // fastFailTestUrl = this._combineUriPaths(this._host, fastFailTestUrl);
      // fastFailTestUrl += (key ? "?routingkey=" + key + "&" : "?") +
      //          "time=" + (new Date()).getTime();
      // Delete these next six lines when g1032962  is fixed...

      fastFailTestUrl = this._combineUriPaths(host, fastFailTestUrl);

      if (key) {
        fastFailTestUrl += '?routingkey=' + key;
      } else {
        fastFailTestUrl += '?time=' + new Date().getTime();
      }

      return {
        'fastFailUrl': fastFailTestUrl,
        'iFrameUrl': iFrameUrl
      };
    },
    _createIframe: function _createIframe(calculatedUrls) {
      var fastFailTestUrl = calculatedUrls.fastFailUrl;
      var that = this;
      this._url = calculatedUrls.iFrameUrl;

      if (this.remoteIframe) {
        domConstruct.destroy(this.remoteIframe);
      }

      this.remoteIframe = domConstruct.create('iframe', {
        id: this.iframeId,
        src: this._url,
        width: 0,
        height: 0,
        style: 'visibility: hidden; display: none;'
      });
      document.body.appendChild(this.remoteIframe); // Check to see if we can tell the server is down or doesn't have the requested version
      // without waiting for the iframe timeout.  Since checkUrl is yet another request,
      // if we have a bunch of HTTP connections going out, we want to delay sending another
      // immediately.

      this._checkUrl = null;
      this._checkUrlTimeout = setTimeout(function () {
        that._checkUrl = new CheckUrlService(fastFailTestUrl).then(function ignore() {
          that._checkUrl = null;
        }, function (err) {
          that._checkUrl = null;

          if (err.dojoType === 'cancel' || err.name === 'CancelError') {
            // If this is cancelled, we don't want
            // to report an error.  We cancelled because
            // we already connected OR we already loaded the iFrame
            // or we know we cannot load the iframe.
            return;
          }

          that._handleLoadTimeoutHitch();
        });
      }, this.IFRAME_TIMEOUT * 0.15); // 15% of IFRAME_TIMEOUT (20s) => (3 sec) - we try
      // each iFrame for 20 seconds, so this seemed like a good compromise to
      // delay the loading of checkJS while still allowing fast detection of missing
      // iFrames.
      // Create a timeout for the iframe successfully loading.

      this._loadTimeoutHandle = setTimeout(function () {
        that._handleLoadTimeoutHitch();
      }, this.IFRAME_TIMEOUT);

      if (!this._messageConnection) {
        this._messageConnection = connect.connect(window, 'message', this, this._handleMessage);
      }
    },
    _handleLoadTimeoutHitch: function _handleLoadTimeoutHitch() {
      this._cleanupFailureChecks();

      if (!this._loaded) {
        if (this._version === '1.0') {
          this._handleLoadTimeout();
        } else {
          var prevVersion = (parseFloat(this._version) - 0.1).toFixed(1).toString();
          Log.info('Unable to load proxy version ' + this._version + ', trying ' + prevVersion + '.');

          this._setVersion(prevVersion);
        }
      }
    },
    createUploadIframeTarget: function createUploadIframeTarget(targetId, callback, context) {
      this._uploadTargetCallbacks[targetId] = lang.hitch(context || this, callback);

      this._postMessage('createUploadIframe;' + targetId);
    },
    cancelUpload: function cancelUpload(targetId) {
      this._postMessage('cancelUpload;' + targetId);
    },
    getUploadIframeProxyUrlPath: function getUploadIframeProxyUrlPath() {
      return this.REMOTE_PROXY + this._version + '/uploadIframeProxy.html';
    },
    sendMessage: function sendMessage(message, endpoint, options) {
      var that = this;
      var id;
      var data;
      var transaction;

      if (this.computeToken) {
        options = options || {};
        options.headers = options.headers || {};
        options.headers.computeToken = this.computeToken;
      }

      id = this._getNewMessageId();
      data = 'sendMessage;' + id + ';' + endpoint + ';';
      data += options ? JSON.stringify(options, null, 0) : '{}';
      data += ';' + message;

      function canceler() {
        if (that._version === '1.0') {
          Log.warn('Cancelling is not supported by the 1.0 remote proxy.');
        } else {
          that._postMessage('abort;' + id);
        }
      }

      transaction = {
        deferred: new Deferred(canceler),
        id: id
      };

      if (this._errorLoading) {
        transaction.deferred.reject('The iFrame did not load properly: ' + this._url);
      } else {
        this._transactions[id] = transaction;

        this._postMessage(data);
      }

      return transaction.deferred;
    },
    setLatency: function setLatency(latency) {
      this._latency = latency;
    },
    setErrorRate: function setErrorRate(errorRate) {
      this._errorRate = errorRate;
    },
    _postMessage: function _postMessage(data) {
      if (this._loaded) {
        this._postMessageNow(data);
      } else {
        this._pendingQueue.push(data);
      }
    },
    _handleMessage: function _handleMessage(event) {
      var idIndex, actionIndex, action, id, messageData;
      var closeData, closeEvent, msgData, msgEvent;

      if (event.source === this.remoteIframe.contentWindow) {
        // event.data format:
        // <action>;<data>
        // Handled actions:
        // ready
        // sendMessageResponse;<id>;<response type>;<response text>
        // uploadIframeMessage;<iframe id>;<data>
        actionIndex = event.data.indexOf(';');
        action = event.data.substring(0, actionIndex);

        if (!action) {
          throw new Error('Client unable to parse message, no action specified: ' + event.data);
        }

        switch (action) {
          case 'ready':
            this._cleanupFailureChecks();

            this._loaded = true;

            this._flushPending();

            break;

          case 'sendMessageResponse':
            this._sendMessageResponse(event, actionIndex);

            break;

          case 'uploadIframeMessage':
            idIndex = event.data.indexOf(';', actionIndex + 1);
            id = event.data.substring(actionIndex + 1, idIndex);
            messageData = event.data.substring(idIndex + 1, event.data.length);

            this._uploadTargetCallbacks[id](messageData);

            break;

          case 'websocketOpened':
            this.onWebsocketOpen();
            break;

          case 'websocketClosed':
            closeData = event.data.substring(actionIndex + 1, event.data.length);
            closeEvent = JSON.parse(closeData);
            this.onWebsocketClose(closeEvent);
            break;

          case 'websocketMsg':
            msgData = event.data.substring(actionIndex + 1, event.data.length);
            msgEvent = JSON.parse(msgData);
            this.onWebsocketMsg(msgEvent);
            break;

          case 'websocketError':
            this.onWebsocketError();
            break;

          default:
            throw new Error('Unknown action: ' + action + ', data: ' + event.data);
        }
      }
    },
    _sendMessageResponse: function _sendMessageResponse(event, actionIndex) {
      // NOTE: If changing this code, you may need to change:
      // _postMessageNow
      var transaction, message;
      var idIndex = event.data.indexOf(';', actionIndex + 1);
      var responseTypeIndex = event.data.indexOf(';', idIndex + 1);
      var id = event.data.substring(actionIndex + 1, idIndex);
      var responseType = event.data.substring(idIndex + 1, responseTypeIndex);
      var messageData = event.data.substring(responseTypeIndex + 1, event.data.length);

      if (!id || !responseType) {
        throw new Error('Invalid message from remote iframe: ' + event.data);
      }

      transaction = this._transactions[id];

      if (!transaction) {
        throw new Error('Unknown transaction id: ' + id + ', from remote iframe: ' + event.data);
      }

      delete this._transactions[id];

      if (!transaction.deferred.isCanceled()) {
        if (responseType === 'success') {
          try {
            // Convert from JSON if there is messageData success is
            // usually JSON, but just in case, wrap it in a try/catch
            message = messageData && JSON.parse(messageData);
          } catch (e) {
            message = messageData;
          }

          message = message === '' ? {} : message;
          transaction.deferred.resolve(message);
          return;
        }

        this.onError(messageData); // Errors are just strings
        // HTTP Status Code if present is added to string object

        transaction.deferred.reject(messageData);
      }
    },
    _cleanupFailureChecks: function _cleanupFailureChecks() {
      if (this._checkUrlTimeout) {
        clearTimeout(this._checkUrlTimeout);
      }

      if (this._loadTimeoutHandle) {
        clearTimeout(this._loadTimeoutHandle);
      }

      if (this._checkUrl && !this._checkUrl.isFulfilled()) {
        this._checkUrl.cancel('Timeout Occurred or iFrame Was Loaded before CheckUrl', false);
      }

      this._checkUrlTimeout = null;
      this._loadTimeoutHandle = null;
      this._checkUrl = null;
    },
    _handleLoadTimeout: function _handleLoadTimeout() {
      var fault, transaction, transactions;

      if (this._loaded) {
        return;
      }

      fault = {
        code: 'IframeLoadTimeout',
        description: 'Iframe proxy loading failed due to an HTTP error',
        message: ''
      };
      transactions = this._transactions;
      this._transactions = {};
      this._pendingQueue = [];

      for (transaction in transactions) {
        if (transactions.hasOwnProperty(transaction)) {
          transactions[transaction].deferred.reject('Timeout while loading the iFrame ' + this._url + ' at ' + new Date());
        }
      }

      this._errorLoading = true;
      this.onError(fault);
    },
    _postMessageNow: function _postMessageNow(data) {
      // NOTE: If changing this code, you may need to change:
      // _sendMessageResponse
      var that = this;
      var id, transaction;

      function doPost() {
        // generate an error response instead of do the real postMessage call
        if (that._errorRate > 0 && Math.random() < that._errorRate && data.indexOf('sendMessage;') === 0) {
          id = data.split(';', 2)[1];
          transaction = that._transactions[id];

          if (transaction) {
            delete that._transactions[id];
            Log.error('Generating simulated error. Request was: ', data);
            var error = '403 Forbidden;403';
            that.onError(error); // Errors are just strings
            // HTTP Status Code if present is added to string object

            transaction.deferred.reject(error);
          }
        } else {
          that.remoteIframe.contentWindow.postMessage(data, that._url);
        }
      }

      if (this._latency) {
        setTimeout(doPost, this._latency);
      } else {
        doPost();
      }
    },
    _flushPending: function _flushPending() {
      this._pendingQueue.forEach(this._postMessageNow, this);

      this._pendingQueue = [];
    },
    _getNewMessageId: function _getNewMessageId() {
      this._messageId += 1;
      return this._messageId.toString();
    },

    /**
     * onError event is fired when a xhr fault is received
     * Designed to aspect on
     * @params fault - a fault object
     * @returns {void}
     */
    onError: function onError()
    /* fault */
    {},
    onWebsocketOpen: function onWebsocketOpen() {},
    onWebsocketMsg: function onWebsocketMsg()
    /* msg */
    {},
    onWebsocketClose: function onWebsocketClose()
    /* event */
    {},
    onWebsocketError: function onWebsocketError() {},

    /**
     * Computes a valid Uri path from valid Uri segments
     * Usage:
     *   _combineUriPaths('/s1/s2/', '/s3/','s4','s5','', 'f1.html?q=1')
     * Will return:
     *  '/s1/s2/s3/s4/s5/f1.html?q=1'
     *
     * If a parameter is not valid Uri path on its own, the returned Uri is not
     * going to be valid.
     * So parameters like "x//y", "x y/", "d%d" would all result in a bad Uri.
     *
     * @param {...string} s - an argument list of path strings to combine
     * @returns {string} a combined uri from the arg list
     */
    _combineUriPaths: function _combineUriPaths()
    /* args */
    {
      var args = Array.prototype.slice.call(arguments);
      var combinedUri = args.reduce(function (a, b) {
        if (a.length === 0) {
          return b;
        } else if (b.length > 0) {
          if (b[0] === '/' && a.lastIndexOf('/') === a.length - 1) {
            b = b.substring(1);
          } else if (b[0] !== '/' && a.lastIndexOf('/') !== a.length - 1) {
            a += '/';
          }
        }

        return a + b;
      });
      return combinedUri;
    }
  });
  IframeProxyClient.WebSocket = declare([], {
    constructor: function constructor(url) {
      var that = this;
      this.proxyClient = IframeProxyClient.WebSocket.proxyClient;

      if (this.proxyClient.computeToken && url.indexOf('?') >= 0) {
        url = url + '&computeToken=' + this.proxyClient.computeToken;
      }

      this.proxyClient._postMessage('connectWebsocket;' + url);

      aspect.after(this.proxyClient, 'onWebsocketOpen', function () {
        that.onopen.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketMsg', function () {
        that.onmessage.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketClose', function () {
        that.onclose.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketError', function () {
        that.onerror.apply(that, arguments);
      }, true);
    },
    onopen: function onopen() {},
    onmessage: function onmessage()
    /* msg */
    {},
    onclose: function onclose()
    /* event */
    {},
    onerror: function onerror()
    /* event */
    {},
    send: function send(data) {
      this.proxyClient._postMessage('websocketSend;' + data);
    },
    close: function close() {
      this.proxyClient._postMessage('closeWebsocket;');
    }
  });
  return IframeProxyClient;
});
//# sourceMappingURL=IframeProxyClient.js.map
